import { useState, type FormEvent } from 'react';
import { FileText, IndianRupee, Percent, Search, Send, Timer } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Skeleton,
  StatCard,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { statusTone } from '@/lib/status';
import {
  QUOTATION_STATUS_LABEL,
  useQuotation,
  useQuotations,
  useQuotationSummary,
  useReviseQuotation,
  useSendQuotation,
  type QuotationLineInput,
  type QuotationStatus,
} from '@/lib/crm-api';
import {
  cn,
  errorMessage,
  fieldErrors,
  formatCompactCurrency,
  formatCurrency,
  formatDateTime,
  formatNumber,
} from '@/lib/utils';

/**
 * Quotations (spec §17, §34).
 *
 * A quotation is a negotiated offer, so the unit price is typed by sales and
 * the tier ladder does not override it — which is exactly why this screen lets
 * a price be entered freely while the cart screens never would.
 *
 * A sent quotation is never edited. Answering a negotiation drafts a **new
 * version** chained to the original, so the history of what was offered
 * survives and either side can point at the version that was accepted.
 */

const STATUS_TABS: Array<{ label: string; value: QuotationStatus[] | null; awaiting?: boolean }> = [
  { label: 'All', value: null },
  { label: 'Drafts', value: ['DRAFT'] },
  { label: 'Awaiting buyer', value: null, awaiting: true },
  { label: 'Negotiation', value: ['NEGOTIATION'] },
  { label: 'Accepted', value: ['ACCEPTED', 'CONVERTED_TO_ORDER'] },
  { label: 'Closed', value: ['REJECTED', 'EXPIRED'] },
];

export function QuotationsPage() {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const activeTab = STATUS_TABS[tab] ?? STATUS_TABS[0]!;

  const quotations = useQuotations({
    page,
    limit: 20,
    search,
    status: activeTab.value ?? undefined,
    awaitingResponseOnly: activeTab.awaiting,
  });
  const summary = useQuotationSummary();

  return (
    <div>
      <PageHeader
        title="Quotations"
        description="Formal offers, their validity and what buyers did with them."
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.isLoading ? (
          [0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-24" />)
        ) : (
          <>
            <StatCard
              label="Drafts"
              value={formatNumber(summary.data?.['draft'] ?? 0)}
              icon={<FileText className="size-5" aria-hidden="true" />}
              tone="neutral"
            />
            <StatCard
              label="Open pipeline"
              value={formatCompactCurrency(summary.data?.['openValue'] ?? 0)}
              icon={<IndianRupee className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Expiring in 3 days"
              value={formatNumber(summary.data?.['expiringSoon'] ?? 0)}
              icon={<Timer className="size-5" aria-hidden="true" />}
              tone="warning"
            />
            <StatCard
              label="Acceptance rate"
              value={`${summary.data?.['acceptanceRatePercent'] ?? 0}%`}
              icon={<Percent className="size-5" aria-hidden="true" />}
              tone="success"
            />
          </>
        )}
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search quotation number or company"
              className="pl-9"
              aria-label="Search quotations"
            />
          </div>

          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Quotation status">
            {STATUS_TABS.map((entry, index) => (
              <button
                key={entry.label}
                type="button"
                role="tab"
                aria-selected={tab === index}
                onClick={() => {
                  setTab(index);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  tab === index
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-muted hover:bg-canvas hover:text-ink',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        {quotations.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : quotations.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load quotations">
              {errorMessage(quotations.error)}
            </ErrorAlert>
          </div>
        ) : (quotations.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No quotations match"
            description="Try a different status or clear the search."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Quotation</Th>
                <Th>Customer</Th>
                <Th>Status</Th>
                <Th className="text-right">Lines</Th>
                <Th className="text-right">Total</Th>
                <Th>Valid until</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {quotations.data?.items.map((quotation) => (
                <tr key={quotation.id} className="hover:bg-canvas/60">
                  <Td>
                    <p className="font-medium text-ink">
                      {quotation.quotationNumber}
                      {quotation.version > 1 && (
                        <span className="ml-1.5 text-xs font-normal text-ink-subtle">
                          v{quotation.version}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-ink-subtle">
                      {quotation.createdBy?.name ?? '—'}
                    </p>
                  </Td>
                  <Td>
                    <p className="text-ink">{quotation.customer?.companyName ?? '—'}</p>
                    <p className="text-xs text-ink-subtle">{quotation.customer?.customerCode}</p>
                  </Td>
                  <Td>
                    <Badge tone={statusTone(quotation.status)}>
                      {QUOTATION_STATUS_LABEL[quotation.status]}
                    </Badge>
                  </Td>
                  <Td className="text-right tabular-nums">{quotation.itemCount}</Td>
                  <Td className="text-right font-medium tabular-nums">
                    {formatCurrency(quotation.totalAmount)}
                  </Td>
                  <Td className="text-sm">
                    {new Date(quotation.validUntil).toLocaleDateString('en-IN', {
                      dateStyle: 'medium',
                    })}
                    {/* Expiry is derived from the date, not a swept status. */}
                    {quotation.isExpired && quotation.status !== 'CONVERTED_TO_ORDER' && (
                      <span className="block text-xs text-danger-700">Lapsed</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelectedId(quotation.id)}>
                      Open
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {quotations.data && quotations.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {quotations.data.pagination.total} quotations · page{' '}
              {quotations.data.pagination.page} of {quotations.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!quotations.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!quotations.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <QuotationDrawer quotationId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ==========================================================================
   Detail drawer
   ========================================================================== */

function QuotationDrawer({
  quotationId,
  onClose,
}: {
  quotationId: string | null;
  onClose: () => void;
}) {
  const detail = useQuotation(quotationId);
  const send = useSendQuotation();
  const [revising, setRevising] = useState(false);

  const quotation = detail.data;

  return (
    <Drawer
      open={Boolean(quotationId)}
      onClose={() => {
        setRevising(false);
        onClose();
      }}
      title={quotation?.quotationNumber ?? 'Quotation'}
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : detail.isError ? (
        <ErrorAlert title="Could not load this quotation">
          {errorMessage(detail.error)}
        </ErrorAlert>
      ) : (
        quotation && (
          <div className="space-y-5">
            <section>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge tone={statusTone(quotation.status)}>
                  {QUOTATION_STATUS_LABEL[quotation.status]}
                </Badge>
                {quotation.version > 1 && <Badge tone="neutral">Version {quotation.version}</Badge>}
                {quotation.isExpired && quotation.status !== 'CONVERTED_TO_ORDER' && (
                  <Badge tone="danger">Lapsed</Badge>
                )}
              </div>

              <p className="font-medium text-ink">{quotation.customer?.companyName ?? '—'}</p>
              <p className="text-xs text-ink-subtle">
                {quotation.customer?.customerCode}
                {quotation.createdBy && ` · drafted by ${quotation.createdBy.name}`}
              </p>
              <p className="mt-1 text-xs text-ink-subtle">
                Valid until{' '}
                {new Date(quotation.validUntil).toLocaleDateString('en-IN', {
                  dateStyle: 'medium',
                })}
                {quotation.sentAt && ` · sent ${formatDateTime(quotation.sentAt)}`}
                {quotation.viewedAt && ` · viewed ${formatDateTime(quotation.viewedAt)}`}
              </p>

              {quotation.rejectionReason && (
                <p className="mt-2 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
                  Declined: {quotation.rejectionReason}
                </p>
              )}

              {quotation.convertedOrder && (
                <p className="mt-2 rounded-lg bg-success-50 px-3 py-2 text-sm text-success-700">
                  Became order {quotation.convertedOrder.orderNumber}.
                </p>
              )}
            </section>

            <section className="rounded-lg border border-line p-4">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                {(
                  [
                    ['Taxable value', quotation.subtotal],
                    ['GST', quotation.taxAmount],
                    ['Freight', quotation.shippingAmount],
                    ['Other charges', quotation.additionalCharges],
                    ['Discount', quotation.discountAmount],
                    ['Total', quotation.totalAmount],
                  ] as Array<[string, number]>
                ).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-ink-subtle">{label}</dt>
                    <dd className="font-medium tabular-nums text-ink">{formatCurrency(value)}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {revising ? (
              <ReviseForm
                quotationId={quotation.id}
                customerId={quotation.customer?.id ?? ''}
                items={quotation.items}
                onDone={() => setRevising(false)}
              />
            ) : (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    disabled={quotation.status !== 'DRAFT' || send.isPending}
                    onClick={() => send.mutate({ quotationId: quotation.id })}
                    title={
                      quotation.status === 'DRAFT'
                        ? undefined
                        : 'Only a draft can be sent. Answer a negotiation with a revision.'
                    }
                  >
                    <Send className="size-4" aria-hidden="true" />
                    {send.isPending ? 'Sending…' : 'Send to buyer'}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={quotation.status === 'CONVERTED_TO_ORDER'}
                    onClick={() => setRevising(true)}
                    title={
                      quotation.status === 'CONVERTED_TO_ORDER'
                        ? 'This quotation is already an order.'
                        : undefined
                    }
                  >
                    Draft a revision
                  </Button>
                </div>

                {send.isError && (
                  <div className="mt-2">
                    <ErrorAlert title="Could not send">{errorMessage(send.error)}</ErrorAlert>
                  </div>
                )}

                <p className="mt-2 text-xs text-ink-subtle">
                  Validity runs from the moment it is sent, not from when it was drafted.
                </p>
              </section>
            )}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">
                Lines ({quotation.items.length})
              </h3>
              <ul className="space-y-2">
                {quotation.items.map((item) => (
                  <li key={item.id} className="rounded-lg border border-line p-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-ink">{item.productName}</p>
                        <p className="text-xs text-ink-subtle">
                          {item.productSku ?? 'Off-catalogue'}
                          {item.hsnCode && ` · HSN ${item.hsnCode}`}
                        </p>
                        <p className="mt-1 text-xs text-ink-muted">
                          {formatNumber(item.quantity)} × {formatCurrency(item.unitPrice)} ·{' '}
                          {item.taxPercent}% GST
                        </p>
                      </div>
                      <p className="shrink-0 font-medium tabular-nums text-ink">
                        {formatCurrency(item.lineTotal)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {quotation.internalNotes && (
              <section>
                <h3 className="mb-1 text-sm font-semibold text-ink">Internal notes</h3>
                <p className="whitespace-pre-wrap rounded-lg bg-canvas p-3 text-sm text-ink-muted">
                  {quotation.internalNotes}
                </p>
                <p className="mt-1 text-xs text-ink-subtle">Never shown to the buyer.</p>
              </section>
            )}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">History</h3>
              <ol className="space-y-2">
                {quotation.statusHistory.map((entry) => (
                  <li key={entry.id} className="rounded-lg border border-line p-3 text-sm">
                    <p className="font-medium text-ink">
                      {QUOTATION_STATUS_LABEL[entry.toStatus]}
                      {entry.fromStatus && (
                        <span className="font-normal text-ink-subtle">
                          {' '}
                          from {QUOTATION_STATUS_LABEL[entry.fromStatus]}
                        </span>
                      )}
                    </p>
                    {entry.notes && <p className="mt-1 text-xs text-ink-muted">{entry.notes}</p>}
                    <p className="mt-1 text-xs text-ink-subtle">
                      {formatDateTime(entry.createdAt)}
                      {entry.changedBy && ` · ${entry.changedBy}`}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )
      )}
    </Drawer>
  );
}

/* ==========================================================================
   Revision
   ========================================================================== */

/**
 * Draft a new version from the current one.
 *
 * Prefilled with the existing lines because a revision is almost always a
 * price change on the same goods, and retyping a ten-line quotation to move one
 * number is how mistakes get made.
 */
function ReviseForm({
  quotationId,
  customerId,
  items,
  onDone,
}: {
  quotationId: string;
  customerId: string;
  items: Array<{
    productId: string | null;
    productName: string;
    productSku: string | null;
    hsnCode: string | null;
    quantity: number;
    unitPrice: number;
    taxPercent: number;
  }>;
  onDone: () => void;
}) {
  const revise = useReviseQuotation();

  const [lines, setLines] = useState<QuotationLineInput[]>(() =>
    items.map((item) => ({
      productId: item.productId ?? undefined,
      productName: item.productName,
      productSku: item.productSku ?? undefined,
      hsnCode: item.hsnCode ?? undefined,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      taxPercent: item.taxPercent,
    })),
  );
  const [notes, setNotes] = useState('');

  const errors = fieldErrors(revise.error);

  const setLine = (index: number, patch: Partial<QuotationLineInput>) =>
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    revise.mutate(
      { quotationId, customerId, items: lines, notes: notes || undefined },
      { onSuccess: () => onDone() },
    );
  };

  return (
    <section className="rounded-lg border border-line p-4">
      <h3 className="mb-1 text-sm font-semibold text-ink">Draft a revision</h3>
      <p className="mb-3 text-xs text-ink-subtle">
        Creates a new version. The original is left exactly as it was.
      </p>

      <form className="space-y-3" onSubmit={handleSubmit}>
        {revise.isError && (
          <ErrorAlert title="Could not draft the revision">{errorMessage(revise.error)}</ErrorAlert>
        )}

        {lines.map((line, index) => (
          <div key={index} className="rounded-lg border border-line p-3">
            <p className="mb-2 text-sm font-medium text-ink">{line.productName}</p>
            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Quantity"
                htmlFor={`qty-${index}`}
                error={errors[`items[${index}].quantity`]}
              >
                <Input
                  id={`qty-${index}`}
                  type="number"
                  min={1}
                  value={line.quantity}
                  onChange={(event) => setLine(index, { quantity: Number(event.target.value) })}
                />
              </Field>
              <Field
                label="Unit price (₹)"
                htmlFor={`price-${index}`}
                error={errors[`items[${index}].unitPrice`]}
              >
                <Input
                  id={`price-${index}`}
                  type="number"
                  step="0.01"
                  min={0}
                  value={line.unitPrice}
                  onChange={(event) => setLine(index, { unitPrice: Number(event.target.value) })}
                />
              </Field>
            </div>
          </div>
        ))}

        <Field label="Note to the buyer" htmlFor="revision-notes">
          <Textarea
            id="revision-notes"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Revised pricing at the quantity you asked for."
          />
        </Field>

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={revise.isPending}>
            {revise.isPending ? 'Saving…' : 'Create revision'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone} disabled={revise.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
